/**
 * SETLIST (F1 — music capability): ordered songs for an occasion with
 * key per occasion. Pure logic shared between UI and tests.
 */

import { prefersFlats, semitonesBetween, transposeContent } from "./chords";

export interface SetlistSongLike {
  id: string;
  song_id: string | null;
  sort_order: number | null;
  key_override?: string | null;
  created_at?: string | null;
}

/** Stable order of setlist: sort_order and tie-broken by creation. */
export function orderSetlistSongs<T extends SetlistSongLike>(rows: readonly T[]): T[] {
  return rows.toSorted((a, b) => {
    const byOrder = (a.sort_order ?? 0) - (b.sort_order ?? 0);
    if (byOrder !== 0) {
      return byOrder;
    }
    return (a.created_at ?? "").localeCompare(b.created_at ?? "");
  });
}

/** Effective key of song in occasion (override > own key). */
export function effectiveKey(
  songKey: string | null | undefined,
  keyOverride: string | null | undefined,
): string | null {
  return keyOverride?.trim() || songKey?.trim() || null;
}

/**
 * Part content (musician chords) transposed to occasion
 * key. If no override (or matching original key), returns
 * the content as-is.
 */
export function partContentForOccasion(
  content: string | null | undefined,
  songKey: string | null | undefined,
  keyOverride: string | null | undefined,
): string {
  if (!content) {
    return "";
  }
  const from = songKey?.trim();
  const to = keyOverride?.trim();
  if (!from || !to || from === to) {
    return content;
  }
  const semitones = semitonesBetween(from, to);
  return transposeContent(content, semitones, prefersFlats(to));
}
